<template>
  <div class="h-full flex">
    <!-- 主导航栏 -->
    <div class="bg-blue-900 flex flex-col min-w-0 transition-all duration-300 z-10 shadow-lg h-full sidebar-nav" :class="isCollapsed ? 'w-16' : 'w-28'">

      <!-- 动态生成导航菜单列表 -->
      <div id="sidebar-menu" class="flex-1 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden py-2.5">
        <!-- 使用v-for动态生成菜单 -->
        <div
          v-for="menu in filteredMainMenuData"
          :key="menu.id"
          :id="`${menu.id}-menu-container`"
          class="menu-item flex flex-col"
          @mouseenter="onMenuEnter(menu.id)"
          @mouseleave="onMenuLeave"
        >
          <!-- 外部链接的菜单项 -->
          <a
            v-if="menu.isExternalLink"
            href="javascript:void(0);"
            @click="openExternalLink(menu.externalUrl)"
            class="h-14 flex items-center justify-center px-2 text-blue-100 hover:text-white hover:bg-blue-600 transition-all mb-1 cursor-pointer clickable-menu"
          >
            <div class="flex flex-col items-center justify-center text-center w-full">
              <i :class="menu.icon + ' text-xl mb-1'"></i>
              <span v-if="!isCollapsed" class="text-xs whitespace-nowrap">{{ menu.name }}</span>
            </div>
          </a>
          
          <!-- 普通菜单项 -->
          <div
            v-else
            :id="`${menu.id}-menu-item`"
            class="h-14 flex items-center justify-center px-2 text-blue-100 hover:text-white hover:bg-blue-600 transition-all mb-1 cursor-pointer"
            :class="{ 'bg-blue-600 text-white': isMenuItemActive(menu), 'clickable-menu': menu.path }"
          >
            <!-- 有直接路径的菜单项用div渲染，通过点击事件导航 -->
            <div v-if="menu.path" @click="navigateTo(menu.path, menu.openInNewTab)" class="w-full h-full flex flex-col items-center justify-center">
              <div class="flex flex-col items-center justify-center text-center w-full">
                <i :class="menu.icon + ' text-xl mb-1'"></i>
                <span v-if="!isCollapsed" class="text-xs whitespace-nowrap">{{ menu.name }}</span>
              </div>
            </div>
            
            <!-- 没有直接路径的菜单项直接渲染div -->
            <div v-else class="flex flex-col items-center justify-center text-center w-full">
              <i :class="menu.icon + ' text-xl mb-1'"></i>
              <span v-if="!isCollapsed" class="text-xs whitespace-nowrap">{{ menu.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 动态生成子菜单部分 -->
    <sub-menu
      v-for="menu in filteredMenuData"
      :key="`sub-${menu.id}`"
      :active="activeMenu === menu.id"
      :menu-style="getMenuStyles(menu.id)"
      :title="menu.name"
      :menu-name="menu.id"
      @submenu-enter="onSubmenuEnter"
      @submenu-leave="onSubmenuLeave"
    >
      <!-- 常规布局子菜单项 -->
      <div
        v-for="subMenu in getRegularSubMenus(menu)"
        :key="subMenu.id"
        class="w-full h-14 flex items-center p-3 text-blue-100 hover:bg-blue-600 hover:text-white rounded-md cursor-pointer"
        :class="{ 'bg-blue-600 text-white': $route.path === subMenu.path }"
        @click="handleMenuClick(menu, subMenu)"
      >
        <i :class="subMenu.icon + ' text-lg mr-3'"></i>
        <span class="text-sm whitespace-nowrap">{{ subMenu.name }}</span>
      </div>
      
      <!-- 网格布局子菜单项 -->
      <div
        v-for="subMenu in getGridSubMenus(menu)"
        :key="subMenu.id"
        class="w-1/3 h-16 flex flex-col items-center justify-center m-0 text-blue-100 bg-opacity-5 bg-white rounded-md shadow transition-all hover:translate-y-[-2px] hover:shadow-md hover:bg-blue-600 hover:text-white cursor-pointer"
        :class="{ 'bg-blue-600 text-white': $route.path === subMenu.path }"
        @click="handleMenuClick(menu, subMenu)"
      >
        <i :class="subMenu.icon + ' text-2xl mb-1 transition-transform duration-200 hover:scale-110'"></i>
        <span class="text-xs whitespace-nowrap text-center">{{ subMenu.name }}</span>
      </div>
    </sub-menu>

    <!-- 摄像头选择弹窗 -->
    <camera-selection-dialog
      :visible.sync="cameraDialogVisible"
      :cameras="availableCameras"
      :bridge-name="currentBridgeName"
      @camera-selected="handleCameraSelected"
      @close="handleCameraDialogClose"
    />
  </div>
</template>

<script>
import SubMenu from './SubMenu.vue'
import CameraSelectionDialog from './common/CameraSelectionDialog.vue'
import menuAPI from '@/api/menu.js'
import { Message, Loading } from 'element-ui'
import { hasAdminAccess, getUserRoles } from '@/utils/permissionUtils'
import VideoUrlManager from '@/utils/videoUrlManager'

export default {
  name: 'SideNav',
  components: {
    SubMenu,
    CameraSelectionDialog
  },
  props: {
    // 可以接收初始折叠状态
    initialCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 内部维护折叠状态
      isCollapsed: this.initialCollapsed,
      // 内部维护活动菜单状态
      activeMenu: null,
      submenuHovered: false,
      hideSubmenuTimer: null,
      // 子菜单位置映射
      submenuPositions: {},
      // 防抖定时器
      updatePositionsTimer: null,
      // 调试信息
      debug: {
        lastMenuPos: null
      },
      // 添加统一事件处理
      resizeTimer: null,
      scrollTimer: null,
      // 动态菜单数据
      menuData: [],
      userRoles: [],
      // 菜单加载状态
      isLoading: false,
      // 用户状态 - 直接在组件内维护
      isLoggedIn: false,
      userId: null,
      user: null,
      // 权限检查定时器
      permissionCheckTimer: null,
      // 摄像头选择弹窗相关
      cameraDialogVisible: false,
      availableCameras: [],
      currentBridgeName: '未知桥梁'
    }
  },
  computed: {
    // 当前用户是否登录 - 直接使用组件内状态
    hasUserLoggedIn() {
      return this.isLoggedIn
    },
    // 根据用户权限过滤菜单数据
    filteredMenuData() {
      return this.menuData.filter(menu => {
        // 如果是"后台管理"菜单，需要检查用户权限
        // if (menu.id === 'home' || menu.id === 'sensors') {
        if (menu.id === 'home') {
          return hasAdminAccess() && menu.subMenus && menu.subMenus.length > 0;
        }
        // 其他菜单正常显示
        if (!this.canSeeMenu(menu)) {
          return false;
        }
        return menu.subMenus && menu.subMenus.length > 0;
      });
    },
    // 根据用户权限过滤主导航菜单
    filteredMainMenuData() {
      return this.menuData.filter(menu => {
        // 如果是"后台管理"菜单，需要检查用户权限
        // if (menu.id === 'home' || menu.id === 'sensors') {
        if (menu.id === 'home') {
          return hasAdminAccess();
        }
        // 其他菜单正常显示
        if (!this.canSeeMenu(menu)) {
          return false;
        }
        return true;
      });
    }
  },
  created() {
    // 从本地存储加载折叠状态
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed !== null) {
      this.isCollapsed = savedCollapsed === 'true';
    }
    
    // 尝试从localStorage读取用户登录状态
    this.checkLocalUserInfo();
    
    // 加载菜单数据
    this.loadMenuData();
    
    // 监听登录状态变化
    this.$root.$on('user-logged-in', this.handleUserLogin);
    
    // 移除视频URL更新事件监听，因为现在改为动态获取
    
    // 监听权限变化，重新加载菜单
    this.$root.$on('permissions-updated', this.handlePermissionsUpdated);
    
    // 定期检查权限状态（每30秒检查一次）
    this.permissionCheckTimer = setInterval(() => {
      this.checkUserPermissions();
    }, 30000);
  },
  mounted() {
    // 初始化所有菜单位置
    this.$nextTick(() => {
      // 延迟计算以确保DOM完全渲染
      setTimeout(() => {
        this.updateAllSubmenuPositions();
      }, 100);
    });
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
    
    // 监听滚动事件
    const sidebarMenu = document.getElementById('sidebar-menu');
    if (sidebarMenu) {
      sidebarMenu.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('resize', this.handleResize);
    const sidebarMenu = document.getElementById('sidebar-menu');
    if (sidebarMenu) {
      sidebarMenu.removeEventListener('scroll', this.handleScroll);
    }
    
    // 清除定时器
    if (this.hideSubmenuTimer) {
      clearTimeout(this.hideSubmenuTimer);
    }
    if (this.updatePositionsTimer) {
      clearTimeout(this.updatePositionsTimer);
    }
    
    // 移除登录状态变化监听
    this.$root.$off('user-logged-in', this.handleUserLogin);
    
    // 移除旧的视频URL更新事件监听
    
    // 移除权限变化监听
    this.$root.$off('permissions-updated', this.handlePermissionsUpdated);
    
    // 清理权限检查定时器
    if (this.permissionCheckTimer) {
      clearInterval(this.permissionCheckTimer);
      this.permissionCheckTimer = null;
    }
  },
  methods: {
    // 尝试从localStorage读取用户登录信息
    checkLocalUserInfo() {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const userData = JSON.parse(userJson);
          this.user = userData;
          this.userId = userData.id;
          this.isLoggedIn = true;
          console.log('从本地存储恢复用户登录状态:', userData.username);
        }
        
        // 检查用户权限状态
        this.checkUserPermissions();
      } catch (error) {
        console.error('读取本地用户信息失败:', error);
      }
    },
    
    // 检查用户权限状态
    // 菜单角色可见性：菜单声明 roles 时，仅当用户角色命中才显示
    canSeeMenu(menu) {
      if (!menu || !Array.isArray(menu.roles) || menu.roles.length === 0) return true;
      const roles = (this.userRoles && this.userRoles.length) ? this.userRoles : getUserRoles();
      if (roles.length) return menu.roles.some(r => roles.indexOf(r) !== -1);
      // 兼容：无角色明细时用 hasAdminAccess(=ADMIN||MONITOR) 判定
      return hasAdminAccess() && menu.roles.some(r => r === 'ADMIN' || r === 'MONITOR');
    },
    checkUserPermissions() {
      try {
        const canAccessAdmin = hasAdminAccess();
        const userRoles = localStorage.getItem('userRoles');
        const roles = userRoles ? JSON.parse(userRoles) : [];
        this.userRoles = roles;
        
        console.log('用户权限检查:', {
          hasAdminAccess: canAccessAdmin,
          roles,
          canAccessAdmin
        });
      } catch (error) {
        console.error('检查用户权限失败:', error);
      }
    },
    
    // 处理权限更新事件
    handlePermissionsUpdated() {
      console.log('权限已更新，重新检查菜单权限');
      this.checkUserPermissions();
      // 重新加载菜单数据以确保权限过滤生效
      this.loadMenuData(true);
    },

    async openExternalLink() {
      // 获取当前选中的桥梁信息
      const bridgeData = localStorage.getItem('selectedBridge');
      if (!bridgeData) {
        Message({
          type: 'warning',
          message: '请先选择一个桥梁',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      const bridge = JSON.parse(bridgeData);
      this.currentBridgeName = bridge.name;
      
      // 显示加载提示
      const loadingInstance = Loading.service({
        lock: true,
        text: `正在获取${bridge.name}的摄像头设备列表...`,
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      
      try {
        // 获取摄像头设备列表
        const result = await VideoUrlManager.getCameraDevicesByBridgeId(bridge.id, bridge.name);
        
        if (result.isValid && result.cameras && result.cameras.length > 0) {
          if (result.cameras.length === 1) {
            // 只有一个摄像头，直接打开
            const videoUrl = VideoUrlManager.buildVideoUrlFromCamera(result.cameras[0]);
            
            if (videoUrl && videoUrl !== VideoUrlManager.DEFAULT_URL) {
              window.open(videoUrl, '_blank');
              Message({
                type: 'success',
                message: `已打开${bridge.name}的视频监控`,
                duration: 2000,
                showClose: true
              });
            } else {
              Message({
                type: 'error',
                message: `摄像头设备配置无效，请联系管理员。`,
                duration: 5000,
                showClose: true
              });
            }
          } else {
            // 多个摄像头，显示选择弹窗
            this.availableCameras = result.cameras;
            this.cameraDialogVisible = true;
            
            Message({
              type: 'info',
              message: `发现${result.cameras.length}个摄像头设备，请选择要查看的摄像头`,
              duration: 3000,
              showClose: true
            });
          }
        } else {
          // 没有找到摄像头设备
          Message({
            type: 'error',
            message: `桥梁：${bridge.name}未配置摄像头设备或设备未启用，请联系管理员。`,
            duration: 5000,
            showClose: true
          });
        }
      } catch (error) {
        console.error('获取摄像头设备列表失败:', error);
        Message({
          type: 'error',
          message: `获取${bridge.name}摄像头设备失败，请稍后重试或联系管理员。`,
          duration: 5000,
          showClose: true
        });
      } finally {
        // 关闭加载提示
        loadingInstance.close();
      }
    },
    
    // 旧版本的openExternalLink方法，保留作为备份
    openExternalLink_Old(url) {
      // 先检查参数
      if (!url) {
        console.error('openExternalLink方法传入了空的URL');
        return;
      }
      
      console.log('打开外部链接, URL类型:', typeof url, 'URL:', url);
      
      // 获取当前桥梁名称
      let bridgeName = "未知桥梁";
      try {
        const bridgeData = localStorage.getItem('selectedBridge');
        if (bridgeData) {
          const bridge = JSON.parse(bridgeData);
          bridgeName = bridge.name || "未知桥梁";
        }
      } catch (error) {
        console.error('获取桥梁名称失败:', error);
      }
      // 检查是否为视频监控菜单
      const videoMenu = this.menuData.find(menu => menu.id === 'videoMonitor');
      
      // 如果是视频监控菜单并且URL已经被VideoUrlManager更新
      if (videoMenu && videoMenu.externalUrl && typeof url === 'string' && url === videoMenu.externalUrl) {
        // 检查URL有效性 - 确保传入的是字符串类型
        const isValidUrl = typeof url === 'string' && this.isValidVideoUrl(url);
        
        
        // 如果URL无效，显示错误通知
        if (!isValidUrl) {
          console.log('视频监控URL无效，显示错误通知');
          Message({
            type: 'error',
            message: `桥梁：${bridgeName}未配置正确的视频监控地址，请联系管理员。`,
            duration: 5000,
            showClose: true
          });
          return;
        }
        
        console.log('打开视频监控链接:', url);
        window.open(url, '_blank');
        return;
      }
      
      // 处理其他外部链接
      if (typeof url === 'string') {
        window.open(url, '_blank');
      } else {
          Message({
            type: 'error',
            message: `桥梁：${bridgeName}未配置正确的视频监控地址，请联系管理员。`,
            duration: 5000,
            showClose: true
          });
      }
    },
    
    // 判断视频监控URL是否有效
    isValidVideoUrl(url) {
      // 检查是否为默认的空URL
      if (!url || url === 'http://' || url === 'https://') {
        return false;
      }
      
      // 检查localStorage中的视频URL更新记录
      try {
        const updateData = localStorage.getItem('videoUrlUpdate');
        if (updateData) {
          const updateInfo = JSON.parse(updateData);
          if (updateInfo.videoUrl === url) {
            // 如果找到匹配的URL，则根据记录的有效性返回
            return updateInfo.isValid !== false;
          }
        }
      } catch (error) {
        console.error('读取视频URL更新记录失败:', error);
      }
      
      // 如果没有匹配的记录，判断URL是否包含必要的参数
      return url.includes('?') && url.length > 10;
    },
    
    // 新增：导航到指定路径
    navigateTo(path, openInNewTab = false) {
      // 检查菜单配置中是否要求在新标签页打开
      const menu = this.menuData.find(m => m.path === path);
      const shouldOpenInNewTab = openInNewTab || (menu && menu.openInNewTab);
      
      if (shouldOpenInNewTab) {
        // 在新标签页中打开
        const routeData = this.$router.resolve({ path });
        window.open(routeData.href, '_blank');
      } else {
        // 在当前页面导航
        // 添加判断，避免重复导航到当前路径
        if (this.$route.path !== path) {
          this.$router.push(path).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              throw err;
            }
          });
        }
      }
    },
    
    // 新增：处理菜单点击事件，显示对应弹窗
    handleMenuClick(menu, subMenu) {
      const targetMenu = subMenu || menu;
      
      // 检查是否有modalTrigger属性，优先处理弹窗触发
      if (targetMenu.modalTrigger) {
        // 通过事件总线通知父组件显示对应弹窗
        const eventData = {
          menuId: targetMenu.id,
          menuName: targetMenu.name,
          modalTrigger: targetMenu.modalTrigger,
          modalTab: targetMenu.modalTab || null,
          path: targetMenu.path
        };
        
        console.log('【SideNav】触发弹窗显示:', eventData);
        this.$root.$emit('menu-modal-trigger', eventData);
        
        // 隐藏子菜单
        this.hideAllSubmenus();
        return; // 弹窗模式不需要路由跳转
      }
      
      // 传统路由跳转模式
      const eventData = {
        menuId: targetMenu.id,
        menuName: targetMenu.name,
        path: targetMenu.path
      };
      
      // 发送菜单点击事件
      this.$root.$emit('menu-clicked', eventData);
      
      // 如果当前路由不是目标路由，则导航到对应路由
      if (targetMenu.path && this.$route.path !== targetMenu.path) {
        this.navigateTo(targetMenu.path);
      }
      
      // 隐藏子菜单
      this.hideAllSubmenus();
    },
    
    // 检查菜单项是否激活
    isMenuItemActive(menu) {
      // 对于外部链接，不考虑激活状态
      if (menu.isExternalLink) return false;
      
      // 使用API中的方法判断路径匹配
      return menuAPI.isMenuActive(menu, this.$route.path);
    },
    
    // 获取常规布局的子菜单
    getRegularSubMenus(menu) {
      if (!menu || !menu.subMenus) return [];
      return menu.subMenus.filter(subMenu => subMenu.layout !== 'grid');
    },
    
    // 获取网格布局的子菜单
    getGridSubMenus(menu) {
      if (!menu || !menu.subMenus) return [];
      return menu.subMenus.filter(subMenu => subMenu.layout === 'grid');
    },
    
    // 根据菜单ID获取样式
    getMenuStyles(menuId) {
      if (menuId === 'statistics') {
        return this.getStatisticsSubmenuStyle(menuId);
      } else if (menuId === 'voice') {
        return this.getVoiceSubmenuStyle(menuId);
      } else {
        return this.getSubmenuStyle(menuId);
      }
    },
    
    // 处理用户登录事件
    handleUserLogin(userData) {
      console.log('用户登录事件触发，重新加载菜单数据', userData);
      // 设置用户信息
      this.user = userData;
      this.userId = userData ? userData.id : null;
      this.isLoggedIn = !!userData;
      
      // 用户登录后重新加载菜单数据
      this.loadMenuData(true);
    },
    
    // 处理本地存储变更事件，用于监听桥梁变化
    handleStorageChange(e) {
      // 检测桥梁变更
      if (e.key === 'selectedBridge') {
        console.log('检测到桥梁变更，重新加载菜单数据');
        this.loadMenuData(true);
      }
    },
    
    // 加载菜单数据
    loadMenuData(forceRefresh = false) {
      // 避免重复加载
      if (this.isLoading && !forceRefresh) return;
      
      this.isLoading = true;
      console.log('开始加载菜单数据...');
      
      // 根据登录状态决定获取方式
      let menuPromise;
      
      if (this.hasUserLoggedIn && this.userId) {
        // 登录用户，获取基于权限的菜单
        console.log('用户已登录，加载用户特定菜单');
        menuPromise = menuAPI.getMenuByUserId(this.userId);
      } else {
        // 未登录用户，获取基础菜单
        console.log('用户未登录，加载基础菜单');
        menuPromise = menuAPI.getMenuData();
      }
      
      menuPromise
        .then(data => {
          this.menuData = data;
          console.log('菜单数据加载完成:', this.menuData.length);
          
          // 数据加载完成后更新菜单位置
          this.$nextTick(() => {
            setTimeout(() => {
              this.updateAllSubmenuPositions();
            }, 100);
          });
        })
        .catch(error => {
          console.error('加载菜单数据失败:', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    getSubmenuStyle(menuName) {
      // 获取子菜单样式
      const position = this.submenuPositions[menuName] || {};
      const sidebarWidth = this.isCollapsed ? 64 : 112; // w-16 = 64px, w-28 = 112px
      
      return {
        top: position.top || '0px',
        left: position.left || (sidebarWidth + 'px'),
        width: '280px',
        zIndex: 9999
      };
    },
    getVoiceSubmenuStyle(menuName) {
      // 获取基本子菜单样式
      const position = this.submenuPositions[menuName] || {};
      const sidebarWidth = this.isCollapsed ? 64 : 112; // w-16 = 64px, w-28 = 112px
      
      // 获取语音值守菜单容器元素
      const voiceContainer = document.getElementById('voice-menu-container');
      
      // 获取矩形信息
      const voiceRect = voiceContainer ? voiceContainer.getBoundingClientRect() : null;
      
      let topValue = voiceRect ? voiceRect.top + 'px' : position.top || '0px';
      
      // 修正: 直接使用语音值守菜单自己的位置，这是最可靠的方式
      // 不依赖于其他菜单的位置关系，避免菜单结构变化时布局错误
      
      return {
        top: topValue,
        left: position.left || (sidebarWidth + 'px'),
        width: '280px',
        zIndex: 9999
      };
    },
    getStatisticsSubmenuStyle(menuName) {
      // 获取基本子菜单样式
      const position = this.submenuPositions[menuName] || {};
      const sidebarWidth = this.isCollapsed ? 64 : 112; // w-16 = 64px, w-28 = 112px
      
      // 获取统计分析菜单容器元素
      const statsContainer = document.getElementById('statistics-menu-container');
      const statsRect = statsContainer ? statsContainer.getBoundingClientRect() : null;
      
      // 使用菜单实际位置作为默认值 - 这是最可靠的方法
      let topValue = statsRect ? statsRect.top + 'px' : position.top || '0px';
      
      return {
        top: topValue,
        left: position.left || (sidebarWidth + 'px'),
        width: '280px',
        zIndex: 9999
      };
    },
    onMenuEnter(menu) {
      // 清除之前的隐藏定时器
      if (this.hideSubmenuTimer) {
        clearTimeout(this.hideSubmenuTimer);
        this.hideSubmenuTimer = null;
      }
      
      // 先设置活动菜单
      this.activeMenu = menu;
      
      // 先高亮菜单项
      this.highlightMenuItem(menu);
      
      // 通知父组件菜单激活状态
      this.$emit('menu-changed', menu);
      
      // 在DOM更新后计算位置
      this.$nextTick(() => {
        this.updateSubmenuPosition(menu);
      });
    },
    onMenuLeave() {
      // 使用延时避免菜单闪烁
      this.hideSubmenuTimer = setTimeout(() => {
        if (!this.submenuHovered) {
          this.activeMenu = null;
          // 移除所有菜单项高亮
          this.removeAllMenuHighlights();
          // 通知父组件菜单激活状态
          this.$emit('menu-changed', null);
        }
      }, 300); // 延长延迟时间
    },
    onSubmenuEnter(menu) {
      this.submenuHovered = true;
      this.activeMenu = menu;
      // 清除任何隐藏定时器
      if (this.hideSubmenuTimer) {
        clearTimeout(this.hideSubmenuTimer);
        this.hideSubmenuTimer = null;
      }
      // 高亮对应的菜单项
      this.highlightMenuItem(menu);
    },
    onSubmenuLeave() {
      this.submenuHovered = false;
      this.hideSubmenuTimer = setTimeout(() => {
        if (!this.submenuHovered) {
          this.activeMenu = null;
          // 移除所有菜单项高亮
          this.removeAllMenuHighlights();
          // 通知父组件菜单激活状态
          this.$emit('menu-changed', null);
        }
      }, 300); // 延长延迟时间
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      // 存储到本地存储
      localStorage.setItem('sidebarCollapsed', this.isCollapsed);
      // 更新所有子菜单位置
      this.$nextTick(() => {
        // 延迟更新以确保DOM更新完成
        setTimeout(() => {
          this.updateAllSubmenuPositions();
        }, 100);
      });
      // 通知父组件折叠状态变化
      this.$emit('collapse-changed', this.isCollapsed);
    },
    hideAllSubmenus() {
      this.activeMenu = null;
      // 移除所有菜单项高亮
      this.removeAllMenuHighlights();
    },
    updateSubmenuPosition(menuName) {
      if (!menuName) return;
      
      // 对于特殊处理的菜单，直接跳过
      if (menuName === 'statistics' || menuName === 'voice') {
        return;
      }
      
      // 获取菜单容器元素
      const menuContainerId = `${menuName}-menu-container`;
      const menuContainer = document.getElementById(menuContainerId);
      
      if (!menuContainer) {
        // console.warn(`找不到菜单元素: ${menuContainerId}`);
        return;
      }
      
      // 获取菜单元素位置和尺寸信息
      const containerRect = menuContainer.getBoundingClientRect();
      
      // 基于侧边栏宽度计算左侧位置
      const sidebarWidth = this.isCollapsed ? 64 : 112; // w-16 = 64px, w-28 = 112px
      
      // 计算顶部位置
      let submenuTop = containerRect.top;
      
      // 确保菜单在可视区域内
      const windowHeight = window.innerHeight;
      const estimatedSubmenuHeight = this.getEstimatedSubmenuHeight(menuName);
      
      // 如果子菜单可能超出窗口底部，则调整位置
      if (submenuTop + estimatedSubmenuHeight > windowHeight) {
        submenuTop = Math.max(10, windowHeight - estimatedSubmenuHeight - 10);
      }
      
      // 对超出顶部的情况也进行处理
      if (submenuTop < 0) {
        submenuTop = 10; // 确保至少在视口顶部有一点边距
      }
      
      // 更新位置数据
      this.$set(this.submenuPositions, menuName, {
        top: submenuTop + 'px',
        left: sidebarWidth + 'px'
      });
    },
    updateAllSubmenuPositions() {
      // 清除之前的定时器
      if (this.updatePositionsTimer) {
        clearTimeout(this.updatePositionsTimer);
      }
      
      // 使用防抖，避免频繁触发位置更新
      this.updatePositionsTimer = setTimeout(() => {
        // 如果没有菜单数据，不执行更新
        if (!this.menuData || this.menuData.length === 0) {
          return;
        }
        
        // 设置要更新的所有菜单项的菜单名称
        // 只更新有子菜单的菜单项位置
        const menuIds = this.filteredMenuData.map(menu => menu.id);
        
        // 逐个更新菜单位置
        menuIds.forEach(menuId => {
          this.updateSubmenuPosition(menuId);
        });
        
        // 如果当前有活动菜单，确保最后更新一次
        if (this.activeMenu) {
          this.updateSubmenuPosition(this.activeMenu);
        }
      }, 50); // 50ms的防抖延迟
    },
    highlightMenuItem(menuName) {
      // 移除所有高亮
      this.removeAllMenuHighlights();
      
      // 高亮指定菜单项
      const menuContainer = document.getElementById(`${menuName}-menu-container`);
      if (menuContainer) {
        menuContainer.classList.add('active');
      } else {
        // console.warn(`无法高亮菜单项: ${menuName}-menu-container 不存在`);
      }
    },
    removeAllMenuHighlights() {
      // 移除所有菜单项的高亮状态
      const menuItems = document.querySelectorAll('.menu-item');
      menuItems.forEach(item => {
        item.classList.remove('active');
      });
    },
    // 动态计算子菜单高度
    getEstimatedSubmenuHeight(menuName) {
      // 默认高度基础值
      const baseHeight = 50;  // 菜单标题和边距的基本高度
      const itemHeight = 56;  // 每个菜单项的估计高度(常规布局)，原为60，现在改为56 (h-14)
      const gridItemHeight = 64; // 网格布局的高度，原为80，现在改为64 (h-16)
      const gridItemsPerRow = 3; // 网格布局每行显示的项目数，从2改为3
      
      // 查找对应的菜单项数据
      const menuItem = this.menuData.find(item => item.id === menuName);
      if (!menuItem) {
        return 300; // 找不到菜单项时的默认高度
      }
      
      // 获取子菜单项
      const subMenus = menuItem.subMenus || [];
      if (subMenus.length === 0) {
        return baseHeight; // 没有子菜单项
      }
      
      // 检查子菜单项的布局类型
      let gridLayoutItems = 0;
      let regularLayoutItems = 0;
      
      subMenus.forEach(item => {
        if (item.layout === 'grid') {
          gridLayoutItems++;
        } else {
          regularLayoutItems++;
        }
      });
      
      // 计算网格布局的行数
      const gridRows = Math.ceil(gridLayoutItems / gridItemsPerRow);
      
      // 计算总高度 = 基础高度 + 常规项高度 + 网格行高度
      const totalHeight = baseHeight + 
                         (regularLayoutItems * itemHeight) + 
                         (gridRows * gridItemHeight);
      
      return totalHeight;
    },
    handleResize() {
      // 使用防抖，避免频繁触发位置更新
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.updateAllSubmenuPositions();
      }, 100);
    },
    handleScroll() {
      // 使用防抖，避免频繁触发位置更新
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
      this.scrollTimer = setTimeout(() => {
        this.updateAllSubmenuPositions();
      }, 100);
    },
    // 保留这些方法的空实现，避免调用错误
    analyzeAllMenuPositions() {
      return [];
    },
    checkMenuPositions() {
      // 空实现
    },
    analyzeMenuStructure() {
      // 空实现
    },
    navigateAndHideSubmenu(path) {
      this.navigateTo(path);
      this.hideAllSubmenus();
    },
    
    // 移除原来的视频URL相关方法，因为现在改为动态获取
    
    /**
     * 处理摄像头选择事件
     * @param {Object} data 选择的摄像头数据
     */
    handleCameraSelected(data) {
      console.log('用户选择了摄像头:', data);
      
      const { camera } = data;
      
      // 构造视频监控URL
      const videoUrl = VideoUrlManager.buildVideoUrlFromCamera(camera);
      
      if (videoUrl && videoUrl !== VideoUrlManager.DEFAULT_URL) {
        // 打开视频监控页面
        window.open(videoUrl, '_blank');
        
        Message({
          type: 'success',
          message: `已打开摄像头"${camera.name || '未命名摄像头'}"的视频监控`,
          duration: 3000,
          showClose: true
        });
      } else {
        Message({
          type: 'error',
          message: `摄像头"${camera.name || '未命名摄像头'}"配置无效，请联系管理员。`,
          duration: 5000,
          showClose: true
        });
      }
      
      // 关闭弹窗
      this.handleCameraDialogClose();
    },
    
    /**
     * 处理摄像头选择弹窗关闭事件
     */
    handleCameraDialogClose() {
      this.cameraDialogVisible = false;
      this.availableCameras = [];
      this.currentBridgeName = '未知桥梁';
    }
  },
  watch: {
    // 监听来自父组件的折叠状态变更
    initialCollapsed(val) {
      this.isCollapsed = val;
      // 更新所有子菜单位置
      this.$nextTick(() => {
        // 延迟更新以确保DOM更新完成
        setTimeout(() => {
          this.updateAllSubmenuPositions();
        }, 100);
      });
    },
    // 监听菜单数据变化
    menuData() {
      // 当菜单数据变化时，更新菜单位置
      this.$nextTick(() => {
        setTimeout(() => {
          this.updateAllSubmenuPositions();
        }, 100);
      });
    }
  }
}
</script>

<style scoped>
/* 主菜单项和子菜单交互 */
.menu-item {
  position: relative;
}

/* 当子菜单显示时，确保主菜单项保持高亮状态 */
.menu-item.active > div {
  background-color: #3182ce; /* bg-blue-600 */
  color: white;
}

/* 自定义滚动条样式 */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 可点击菜单项的下划线样式 */
.clickable-menu .text-xs {
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  text-decoration-style: solid;
}
</style> 